import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Typography, Table, Button, Tag, Space, Input, Select, Row, Col, message, Popconfirm, Tabs } from 'antd';
import { 
  UserOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  CopyOutlined,
  TeamOutlined,
  EyeOutlined,
  SettingOutlined
} from '@ant-design/icons';
import AddUser from './AddUser';
import EditUser from './EditUser';
import VisitorAccount from './VisitorAccount';
import VisitorList from './VisitorList';
import {
  getAllUsers,
  deleteUser,
  setFilters,
  clearError,
  clearSuccess
} from '../../features/account/userManagementSlice';

const { Title, Text } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;

const UserManagement = () => {
  const dispatch = useDispatch();
  const { 
    users, 
    pagination, 
    roleCounts, 
    filters, 
    loading, 
    error, 
    success, 
    operationType 
  } = useSelector((state) => state.userManagement);

  const [addUserVisible, setAddUserVisible] = useState(false);
  const [editUserVisible, setEditUserVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    dispatch(getAllUsers(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (success && operationType === 'delete') {
      message.success('User deleted successfully!');
      dispatch(clearSuccess());
    }
  }, [success, operationType, dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleSearch = (value) => {
    dispatch(setFilters({ search: value, page: 1 }));
  };

  const handleRoleFilter = (role) => {
    dispatch(setFilters({ role, page: 1 }));
  };

  const handlePageChange = (page, pageSize) => {
    dispatch(setFilters({ page, limit: pageSize }));
  };

  const handleRefresh = () => {
    dispatch(getAllUsers(filters));
  };

  const handleDeleteUser = async (userId) => {
    try {
      await dispatch(deleteUser(userId)).unwrap();
    } catch (error) {
      // Error handled by useEffect
    }
  };

  const handleAddUserSuccess = () => {
    setAddUserVisible(false);
    // Refresh the user list
    dispatch(getAllUsers(filters));
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserVisible(true);
  };

  const handleEditUserSuccess = () => {
    setEditUserVisible(false);
    setSelectedUser(null);
    // Refresh the user list
    dispatch(getAllUsers(filters));
  };

  const handleEditUserCancel = () => {
    setEditUserVisible(false);
    setSelectedUser(null);
  };

  const handleCopyApiKey = (apiKey) => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey).then(() => {
        message.success('API key copied to clipboard!');
      }).catch(() => {
        message.error('Failed to copy API key');
      });
    } else {
      message.warning('No API key available to copy');
    }
  };

  const columns = [
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <UserOutlined className="text-blue-600" />
          </div>
          <div>
            <div className="font-medium">{text}</div>
            <div className="text-sm text-gray-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        const roleColors = {
          root: 'red',
          admin: 'blue',
          omega: 'gold',
          devuser: 'green',
          desuser: 'purple',
          prouser: 'orange',
          visitor: 'gray',
        };
        const roleNames = {
          root: 'ROOT',
          admin: 'ADMIN',
          omega: 'OMEGA',
          devuser: 'DEV USER',
          desuser: 'DES USER',
          prouser: 'PRO USER',
          visitor: 'VISITOR',
        };
        return <Tag color={roleColors[role]}>{roleNames[role]}</Tag>;
      },
    },
    {
      title: 'API Key',
      dataIndex: 'apiKey',
      key: 'apiKey',
      render: (apiKey) => (
        <div className="flex items-center space-x-2">
          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
            {apiKey ? `${apiKey.substring(0, 8)}...` : 'N/A'}
          </span>
          {apiKey && (
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyApiKey(apiKey)}
              className="text-gray-500 hover:text-blue-600"
              title="Copy full API key"
            />
          )}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'date',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.role !== 'root' && (
            <Button 
              size="small" 
              icon={<EditOutlined />} 
              type="link"
              onClick={() => handleEditUser(record)}
            >
              Edit
            </Button>
          )}
          {record.role !== 'root' && (
            <Popconfirm
              title="Delete User"
              description="Are you sure you want to delete this user? This action cannot be undone."
              onConfirm={() => handleDeleteUser(record._id)}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button 
                size="small" 
                icon={<DeleteOutlined />} 
                type="link" 
                danger
                loading={loading && operationType === 'delete'}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-600 rounded-full flex items-center justify-center">
              <TeamOutlined className="text-xl text-white" />
            </div>
            <div>
              <Title level={3} className="mb-1">User Management</Title>
              <Text className="text-gray-600">Manage system users, roles, and visitor restrictions</Text>
            </div>
          </div>
          <Space>
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setAddUserVisible(true)}
            >
              Add User
            </Button>
          </Space>
        </div>
      </Card>

      {/* Tabs for different management sections */}
      <Card className="shadow-soft border border-border-light rounded-xl">
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          size="large"
        >
          {/* System Users Tab */}
          <TabPane 
            tab={
              <span>
                <UserOutlined />
                System Users
              </span>
            } 
            key="users"
          >
            {/* Filters */}
            <Card className="mb-6" size="small">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={12} md={8}>
                  <Search
                    placeholder="Search users..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    onSearch={handleSearch}
                    defaultValue={filters.search}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Select
                    placeholder="Filter by role"
                    allowClear
                    style={{ width: '100%' }}
                    value={filters.role === 'all' ? undefined : filters.role}
                    onChange={handleRoleFilter}
                  >
                    <Select.Option value="admin">Admin</Select.Option>
                    <Select.Option value="omega">Omega</Select.Option>
                    <Select.Option value="devuser">Developer</Select.Option>
                    <Select.Option value="desuser">Designer</Select.Option>
                    <Select.Option value="prouser">Pro User</Select.Option>
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Select
                    placeholder="Filter by status"
                    allowClear
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="active">Active</Select.Option>
                    <Select.Option value="inactive">Inactive</Select.Option>
                  </Select>
                </Col>
              </Row>
            </Card>

            {/* Users Table */}
            <Card 
              title="System Users"
              size="small"
              extra={
                <Text className="text-sm text-gray-500">
                  {pagination.totalUsers} users total
                </Text>
              }
            >
              <Table
                dataSource={users}
                columns={columns}
                loading={loading}
                rowKey="_id"
                pagination={{
                  current: pagination.currentPage,
                  total: pagination.totalUsers,
                  pageSize: pagination.limit,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} users`,
                  onChange: handlePageChange,
                  onShowSizeChange: handlePageChange,
                }}
                scroll={{ x: 800 }}
              />
            </Card>
          </TabPane>

          {/* Visitor Activity Tab */}
          <TabPane 
            tab={
              <span>
                <EyeOutlined />
                Visitor Activity
              </span>
            } 
            key="visitor-activity"
          >
            <VisitorList />
          </TabPane>

          {/* Visitor Settings Tab */}
          <TabPane 
            tab={
              <span>
                <SettingOutlined />
                Visitor Settings
              </span>
            } 
            key="visitor-settings"
          >
            <VisitorAccount />
          </TabPane>
        </Tabs>
      </Card>

      {/* Add User Modal */}
      <AddUser
        visible={addUserVisible}
        onCancel={() => setAddUserVisible(false)}
        onSuccess={handleAddUserSuccess}
      />

      {/* Edit User Modal */}
      <EditUser
        visible={editUserVisible}
        onCancel={handleEditUserCancel}
        onSuccess={handleEditUserSuccess}
        user={selectedUser}
      />

      {/* Coming Soon Notice */}
      <Card className="shadow-soft border border-border-light rounded-xl bg-blue-50">
        <div className="text-center py-8">
          <UserOutlined className="text-4xl text-blue-500 mb-4" />
          <Title level={4} className="text-blue-700 mb-2">Advanced Features Coming Soon</Title>
          <Text className="text-blue-600">
            User analytics, advanced visitor tracking, bulk operations, and detailed activity logs 
            will be available in the next update.
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default UserManagement;